import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  RefreshCw,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Search,
  Building2,
  X,
  AlertTriangle,
  Archive,
  RotateCcw,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Plus,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  fetchDepartements,
  previewDepartementsExcel,
  uploadDepartementsExcel,
  toggleDepartementStatus,
  type NomenclatureDepartement,
  type DepartementUploadPreview,
} from '@/services/nomenclatureService';

export const DepartementsPage: React.FC = () => {
  const { tokens } = useAuthStore();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.DEPARTMENTS_MANAGE);
  const token = tokens?.accessToken;

  // State
  const [departements, setDepartements] = useState<NomenclatureDepartement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(true); // Show archived by default
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Preview modal state
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    preview: DepartementUploadPreview | null;
    file: File | null;
  }>({
    isOpen: false,
    preview: null,
    file: null,
  });

  // Toggle status state
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Archive confirmation modal state
  const [archiveConfirmModal, setArchiveConfirmModal] = useState<{
    isOpen: boolean;
    departement: NomenclatureDepartement | null;
  }>({
    isOpen: false,
    departement: null,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load departements data
  const loadDepartements = async () => {
    if (!token) {
      setError('Vous devez etre connecte pour acceder a cette page');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchDepartements(token, true);
      setDepartements(data);
    } catch (err) {
      console.error('Erreur lors du chargement des departements:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadDepartements();
    }
  }, [token]);

  // File selection handler - preview first
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError('Veuillez selectionner un fichier Excel (.xlsx ou .xls)');
      return;
    }

    if (!token) {
      setError('Vous devez etre connecte pour importer des donnees');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Get preview first
      const preview = await previewDepartementsExcel(file, token);
      setPreviewModal({
        isOpen: true,
        preview,
        file,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'analyse du fichier");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Confirm upload after preview
  const handleConfirmUpload = async () => {
    if (!previewModal.file || !token) return;

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await uploadDepartementsExcel(previewModal.file, token);
      setSuccess(result.message);
      setPreviewModal({ isOpen: false, preview: null, file: null });
      await loadDepartements();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'import");
      setPreviewModal({ isOpen: false, preview: null, file: null });
    } finally {
      setIsUploading(false);
    }
  };

  // Toggle department status (archive/unarchive)
  const handleToggleStatus = async (departement: NomenclatureDepartement) => {
    if (!token) return;

    // If archiving (currently active), show confirmation modal
    if (departement.isActive) {
      setArchiveConfirmModal({ isOpen: true, departement });
      return;
    }

    // If unarchiving, proceed directly
    await performToggleStatus(departement);
  };

  // Actually perform the toggle
  const performToggleStatus = async (departement: NomenclatureDepartement) => {
    if (!token) return;

    setTogglingId(departement.id);
    setError(null);

    try {
      await toggleDepartementStatus(departement.id, token);
      // Update local state
      setDepartements((prev) =>
        prev.map((d) =>
          d.id === departement.id ? { ...d, isActive: !d.isActive } : d
        )
      );
      setSuccess(
        departement.isActive
          ? `"${departement.labelFr}" a ete archive`
          : `"${departement.labelFr}" a ete reactive`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du changement de statut');
    } finally {
      setTogglingId(null);
    }
  };

  // Handle archive confirmation
  const handleConfirmArchive = async () => {
    if (archiveConfirmModal.departement) {
      await performToggleStatus(archiveConfirmModal.departement);
    }
    setArchiveConfirmModal({ isOpen: false, departement: null });
  };

  // Filter departements
  const getFilteredDepartements = () => {
    let filtered = departements;

    if (!showInactive) {
      filtered = filtered.filter((d) => d.isActive);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((d) => d.labelFr.toLowerCase().includes(term));
    }

    return filtered;
  };

  const filteredDepartements = getFilteredDepartements();
  const activeCount = departements.filter((d) => d.isActive).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Departements</h1>
          <p className="text-gray-500 mt-1">
            Gerez les departements de votre etablissement
          </p>
        </div>
        <button
          onClick={loadDepartements}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-700">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Building2 className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Departements</h2>
                <p className="text-sm text-gray-500">
                  {activeCount} departements actifs sur {departements.length} total
                </p>
              </div>
            </div>

            {/* Upload Button */}
            {canManage && (
              <label className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700 disabled:opacity-50">
                {isUploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Analyse...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Importer Excel
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  disabled={isUploading}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="p-4 bg-blue-50 border-b border-blue-100">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-2">Comment importer les departements :</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>
                  Telechargez le fichier Excel depuis le site E-DEP{' '}
                  <a
                    href="https://e-dep.enssup.gov.ma"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline font-medium"
                  >
                    (e-dep.enssup.gov.ma)
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>Menu <strong>Nomenclature</strong> → <strong>Departement</strong></li>
                <li>Exportez le fichier Excel et importez-le ici</li>
              </ol>
              <p className="mt-2 text-blue-600">
                <strong>Note :</strong> Le fichier doit contenir une colonne avec le nom du departement.
                Les departements absents du nouveau fichier seront archives (non supprimes).
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="p-4 border-b border-gray-200 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Afficher les departements archives
          </label>
        </div>

        {/* Data Table */}
        <div className="max-h-[500px] overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
            </div>
          ) : filteredDepartements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {departements.length === 0
                ? "Aucun departement importe. Importez un fichier Excel pour commencer."
                : `Aucun resultat trouve pour "${searchTerm}"`}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Departement
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredDepartements.map((departement, index) => (
                  <tr
                    key={departement.id}
                    className={`hover:bg-gray-50 ${!departement.isActive ? 'bg-gray-50 opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{departement.labelFr}</td>
                    <td className="px-4 py-3">
                      {canManage ? (
                        <button
                          onClick={() => handleToggleStatus(departement)}
                          disabled={togglingId === departement.id}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                            departement.isActive
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          } disabled:opacity-50`}
                          title={departement.isActive ? 'Cliquez pour archiver' : 'Cliquez pour reactiver'}
                        >
                          {togglingId === departement.id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : departement.isActive ? (
                            <Archive className="w-3 h-3" />
                          ) : (
                            <RotateCcw className="w-3 h-3" />
                          )}
                          {departement.isActive ? 'Actif' : 'Archive'}
                        </button>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            departement.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {departement.isActive ? 'Actif' : 'Archive'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Preview Confirmation Modal */}
      {previewModal.isOpen && previewModal.preview && (
        <PreviewModal
          isOpen={previewModal.isOpen}
          preview={previewModal.preview}
          fileName={previewModal.file?.name || ''}
          isLoading={isUploading}
          onConfirm={handleConfirmUpload}
          onCancel={() => setPreviewModal({ isOpen: false, preview: null, file: null })}
        />
      )}

      {/* Archive Confirmation Modal */}
      {archiveConfirmModal.isOpen && archiveConfirmModal.departement && (
        <ArchiveConfirmModal
          isOpen={archiveConfirmModal.isOpen}
          departementName={archiveConfirmModal.departement.labelFr}
          isLoading={togglingId === archiveConfirmModal.departement.id}
          onConfirm={handleConfirmArchive}
          onCancel={() => setArchiveConfirmModal({ isOpen: false, departement: null })}
        />
      )}
    </div>
  );
};

// Expandable Section Component for Preview Modal
interface ExpandableSectionProps {
  title: string;
  count: number;
  items: string[];
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
  borderColor: string;
  defaultExpanded?: boolean;
}

const ExpandableSection: React.FC<ExpandableSectionProps> = ({
  title,
  count,
  items,
  icon,
  bgColor,
  textColor,
  borderColor,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (count === 0) return null;

  return (
    <div className={`rounded-lg border ${borderColor} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 ${bgColor} ${textColor} hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium">{count}</span>
          <span>{title}</span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>
      {expanded && items.length > 0 && (
        <div className={`px-3 py-2 bg-white border-t ${borderColor}`}>
          <ul className="space-y-1 max-h-32 overflow-y-auto">
            {items.map((item, index) => (
              <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0"></span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Preview Modal Component
interface PreviewModalProps {
  isOpen: boolean;
  preview: DepartementUploadPreview;
  fileName: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  preview,
  fileName,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const hasArchive = preview.toArchive > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onCancel} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full transform transition-all">
          {/* Header */}
          <div className={`px-6 py-4 border-b ${hasArchive ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'} rounded-t-xl`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${hasArchive ? 'bg-amber-100' : 'bg-blue-100'}`}>
                {hasArchive ? (
                  <AlertTriangle className={`w-5 h-5 ${hasArchive ? 'text-amber-600' : 'text-blue-600'}`} />
                ) : (
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmer l'import</h3>
                <p className="text-sm text-gray-600 truncate max-w-[300px]" title={fileName}>
                  Fichier: {fileName}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-3">
            <p className="text-sm text-gray-600 mb-4">
              Voici le detail des modifications qui seront effectuees :
            </p>

            {/* New Departments */}
            <ExpandableSection
              title="nouveau(x) departement(s) a ajouter"
              count={preview.newCount}
              items={preview.newDepartements}
              icon={<Plus className="w-4 h-4" />}
              bgColor="bg-green-50"
              textColor="text-green-700"
              borderColor="border-green-200"
              defaultExpanded={preview.newCount <= 5}
            />

            {/* Existing Departments */}
            <ExpandableSection
              title="departement(s) existant(s) (ignores)"
              count={preview.existingCount}
              items={preview.existingDepartements}
              icon={<CheckCircle className="w-4 h-4" />}
              bgColor="bg-blue-50"
              textColor="text-blue-700"
              borderColor="border-blue-200"
              defaultExpanded={false}
            />

            {/* To Reactivate */}
            <ExpandableSection
              title="departement(s) archive(s) a reactiver"
              count={preview.toReactivate}
              items={preview.toReactivateDepartements}
              icon={<RotateCcw className="w-4 h-4" />}
              bgColor="bg-purple-50"
              textColor="text-purple-700"
              borderColor="border-purple-200"
              defaultExpanded={preview.toReactivate <= 5}
            />

            {/* To Archive */}
            <ExpandableSection
              title="departement(s) a archiver"
              count={preview.toArchive}
              items={preview.toArchiveDepartements}
              icon={<Archive className="w-4 h-4" />}
              bgColor="bg-amber-50"
              textColor="text-amber-700"
              borderColor="border-amber-200"
              defaultExpanded={preview.toArchive <= 5}
            />

            {/* Warning message for archiving */}
            {preview.toArchive > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">
                  <strong>Attention :</strong> Les departements archives ne seront pas supprimes
                  et pourront etre reactives ulterieurement depuis le tableau.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${
                hasArchive ? 'bg-amber-600 hover:bg-amber-700' : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {isLoading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Confirmer l'import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Archive Confirmation Modal Component
interface ArchiveConfirmModalProps {
  isOpen: boolean;
  departementName: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ArchiveConfirmModal: React.FC<ArchiveConfirmModalProps> = ({
  isOpen,
  departementName,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onCancel} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full transform transition-all">
          {/* Header with soft amber gradient */}
          <div className="px-6 py-5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 rounded-t-xl">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Archive className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Archiver ce departement ?</h3>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            <p className="text-gray-700 mb-4">
              Vous etes sur le point d'archiver <strong className="text-gray-900">"{departementName}"</strong>.
            </p>

            {/* Reassurance message */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <RotateCcw className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Pas de panique !</p>
                <p>
                  Vous pourrez toujours le reactiver plus tard en cliquant simplement sur
                  le bouton <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium text-gray-600"><RotateCcw className="w-3 h-3" /> Archive</span> dans le tableau.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Archivage...
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4" />
                  Oui, archiver
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartementsPage;
